import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type { ClientDeliverableSheetLink } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';

/**
 * Where a tracker's Google Sheets mirror lives, read ONCE per mirror for the
 * whole app.
 *
 * ⚠️ **THIS WAS "the Client Deliverables tracker's mirror" AND NOW SERVES EVERY
 * MIRROR IN `SHEET_READERS`**, chosen by the `source` argument whose header
 * below records why it is a parameter rather than another copy of this file.
 * ⚠️ *SUPERSEDED 2026-09-13: "**AND NOW SERVES TWO** — that one and the Future
 * Billables one."* **Count `SHEET_READERS`, never this paragraph** — a source is
 * one line in that map, so a number written here goes stale the next time
 * somebody adds one and nothing fails when it does. Everything from here to that
 * header is unchanged and applies per source: one request, one cached answer,
 * failures never cached.
 *
 * ── WHY A MODULE-LEVEL CACHE AND NOT A PROVIDER ───────────────────────────
 *
 * Two surfaces need this answer — the button on `/client-deliverables` and the
 * sidebar row, and the sidebar is mounted on EVERY signed-in route. A provider
 * is the shape `XeroProvider` uses and would be the tidier answer, but it has to
 * be mounted, and mounting it means editing `App.tsx`. A module-level cache gets
 * the same property — N callers, one request — with no mounting at all, and
 * without a second thing to remember when a third caller appears.
 *
 * ── IT IS READ ONCE AND NOT POLLED ────────────────────────────────────────
 *
 * The value is derived from two environment variables on the server. It cannot
 * change under a session; it changes when somebody redeploys. Polling would
 * spend a request a minute to observe something that changes twice a year, and
 * the sidebar would be the thing spending it, on every page.
 *
 * ── FAILURE IS NOT CACHED, AND THAT IS DELIBERATE ─────────────────────────
 *
 * A resolved answer is remembered for the life of the tab; a rejected one is
 * not. Caching a failure would let one blip — a 429 from a viewport loop, a
 * dropped connection on a slow first paint — hide the affordance for the whole
 * session with nothing on screen to say why. The next mount asks again.
 *
 * ⚠️ **Every failure degrades to "no link", never to an error state**, and the
 * caller renders nothing. That is the same decision as a `null` URL: this is an
 * optional way to reach a document, and a red banner about a mirror the reader
 * may not use would be louder than the thing it reports. A 404 is not even a
 * failure — it is what a server predating this route answers.
 */

/**
 * ── WHICH MIRROR — A PARAMETER, NOT A FOURTH COPY OF THIS FILE ────────────
 *
 * ✅ **PARAMETERISED 2026-09-04, and the alternative was weighed rather than
 * skipped.** This module was hard-wired to `api.clientDeliverableSheet()`, and
 * the two pages that came after it each FORKED a local reader —
 * `use-revenue-tracker-sheet.ts` and `AccountsDashboardPage`'s own — both saying
 * in as many words that *"parameterising the shared hook would be the tidier
 * answer and is an edit to a file this change does not own."* That was a
 * SCOPING reason, not a design one, and it has now been paid three times.
 *
 * **A sibling hook was the other option offered and was declined**: it would be
 * a fourth ~220-line file whose cache, its signed-in gate, its
 * failure-is-not-cached rule and its `?? NO_SHEET` guard all have to stay in
 * step with this one, and **nothing anywhere would notice when they stopped**.
 * A parameter with a default keeps one implementation of every one of those
 * decisions.
 *
 * ⚠️ **THE DEFAULT IS WHAT MAKES THIS A NON-CHANGE FOR EVERY EXISTING CALLER.**
 * `Sidebar`, `OpenTrackerSheet` and `ClientDeliverablesPage` all call
 * `useTrackerSheetLink()` with no argument and get byte-identical behaviour —
 * same endpoint, same cache, same single request between them.
 *
 * ⚠️ **A SOURCE NAMES AN ENDPOINT HERE; A CALLER NEVER PASSES A FETCHER.** A
 * function prop would be a fresh identity on every render, so the cache would
 * have nothing stable to key on and the effect below would re-run for ever. The
 * union is also what makes a typo a compile error rather than a request nobody
 * sees fail.
 *
 * ⚠️ **THE TWO EXISTING FORKS ARE DELIBERATELY NOT MIGRATED.** `AccountsDashboard`
 * and the revenue tracker each read a WIDER shape than this one hands back —
 * the accounts link carries per-section tab ids and the revenue link carries
 * `lastSyncedAt`, and the revenue reader's whole `lastSyncedKnown` flag exists
 * to tell an ABSENT key from a `null` value. Folding either in here would mean
 * widening `TrackerSheetState` for readers that do not want it, and would be a
 * behaviour change on two live pages wearing a refactor's clothes. If they are
 * ever unified, that is its own change with its own verification.
 */
export type TrackerSheetSource =
  | 'client-deliverables'
  | 'future-billables'
  | 'sales-tracker'
  | 'companies';

/**
 * ⚠️ **THE ONE PLACE A SOURCE BECOMES A REQUEST.** Every entry returns the same
 * three-key shape — see `FutureBillablesSheetLink` and `SalesTrackerSheetLink`
 * in `@/lib/types`. A source whose response ever gains a key needs a decision
 * here, not a cast.
 *
 * ⚠️ **THE ALIASES ARE NOT EQUALLY WELL EVIDENCED, AND THE MAP CANNOT SHOW IT.**
 * The other two were read off live controllers; `sales-tracker` was added
 * on 2026-09-13 while its route was still being built, so its shape is what was
 * asked for rather than what came back. That costs nothing here — an unbuilt
 * route 404s, the `.catch` below resolves it to `NO_SHEET`, and the caller
 * renders nothing, which is exactly what an unconfigured mirror does. **Read the
 * type's own header before treating the shape as settled.**
 */
const SHEET_READERS: Record<
  TrackerSheetSource,
  () => Promise<{ data?: ClientDeliverableSheetLink }>
> = {
  'client-deliverables': () => api.clientDeliverableSheet(),
  'future-billables': () => api.futureBillablesSheet(),
  'sales-tracker': () => api.salesTrackerSheet(),
  // Added 2026-10-18 alongside its route; until the backend deploys it 404s,
  // which resolves to NO_SHEET exactly as an unconfigured mirror does.
  companies: () => api.companiesSheet(),
};

/** What every failure and every unconfigured box resolves to. */
const NO_SHEET: ClientDeliverableSheetLink = {
  configured: false,
  spreadsheetUrl: null,
  spreadsheetPreviewUrl: null,
};

/**
 * The settled answer PER SOURCE, once we have one. Never holds a failure.
 *
 * ⚠️ **KEYED, AND THAT IS THE WHOLE RISK THE PARAMETER INTRODUCES.** A single
 * shared slot would hand whichever page mounted second the OTHER page's
 * spreadsheet — a wrong document, framed, with nothing failing and nothing on
 * screen to say so. That is precisely the hazard the two forks were written to
 * avoid, so it is closed by the data structure rather than by a rule.
 */
const cached = new Map<TrackerSheetSource, ClientDeliverableSheetLink>();

/** The request in flight per source, so simultaneous mounts share one. */
const inFlight = new Map<TrackerSheetSource, Promise<ClientDeliverableSheetLink>>();

function readSheetLink(source: TrackerSheetSource): Promise<ClientDeliverableSheetLink> {
  const settled = cached.get(source);
  if (settled) return Promise.resolve(settled);

  const pending = inFlight.get(source);
  if (pending) return pending;

  /*
   * ⚠️ NO `AbortSignal`, and this is the one place in this app where that is
   * right rather than an oversight. The house rule exists because a component's
   * own fetch must not outlive it — but this request belongs to no component.
   * Aborting it when ONE subscriber unmounts would cancel it for every other
   * subscriber still on screen, which on this app means the sidebar killing the
   * page's request (or the reverse) purely by navigating.
   *
   * What the rule actually protects against is guarded below instead: each
   * subscriber drops the result if it unmounted first, so no state is set on a
   * dead component. The request itself is a single tiny read that runs at most
   * once per tab.
   */
  const request = SHEET_READERS[source]()
    .then((response) => {
      /*
       * ⚠️ `?? NO_SHEET` RATHER THAN `response.data` DIRECTLY, AND IT IS NOT
       * DEFENSIVE PADDING — it is the difference between one absent key and a
       * blank page.
       *
       * Every subscriber reads `.spreadsheetUrl` off whatever this resolves to,
       * with no guard of its own, and both subscribers are mounted at once: the
       * sidebar row and the tracker's header button. So a resolved `undefined`
       * is a TypeError inside a render, which React escalates to unmounting the
       * whole tree — the tracker page goes blank because a decorative link could
       * not be read.
       *
       * The contract says `data` is always present, and the .catch below covers
       * a throw. What neither covers is a 200 carrying a body we did not expect:
       * a server predating this route, a proxy answering for it, a shape change.
       * This repo's standing rule is that a 200 proves acceptance, never the
       * shape of what came back, and it has paid for that three times on Xero.
       *
       * Resolving to NO_SHEET degrades exactly as an unconfigured box does —
       * both affordances are simply absent — which is already a supported,
       * rendered state rather than a new branch nobody has seen.
       */
      const settledNow = response.data ?? NO_SHEET;
      cached.set(source, settledNow);
      inFlight.delete(source);
      return settledNow;
    })
    .catch(() => {
      // Not written to `cached`, so the next mount retries. See the header.
      inFlight.delete(source);
      return NO_SHEET;
    });

  inFlight.set(source, request);
  return request;
}

/** What a subscriber sees. `previewLink` is the read-only form of `link`. */
interface TrackerSheetState {
  loading: boolean;
  link: string | null;
  previewLink: string | null;
}

/**
 * ⚠️ THE ONE PLACE THE OPTIONAL KEY IS NORMALISED.
 *
 * `spreadsheetPreviewUrl` is declared optional on the response type, because a
 * server predating it omits the key entirely — so `undefined` and `null` both
 * reach here and both mean the same thing: there is nothing to embed. Collapsing
 * them once, at this boundary, is what lets every component below take a plain
 * `string | null` and gate on `!== null` without knowing the key can be missing.
 *
 * Doing it at each call site instead would put the same `?? null` in N places,
 * and the one that got forgotten would hand an iframe `undefined` — which does
 * not throw and does not warn. It resolves to a relative URL and quietly frames
 * THIS app inside itself.
 */
function previewOf(sheet: ClientDeliverableSheetLink): string | null {
  return sheet.spreadsheetPreviewUrl ?? null;
}

/**
 * What a subscriber starts from: the settled answer for THIS source when the tab
 * already has one, and the pending state otherwise.
 *
 * ⚠️ **`loading: true` here is not a guess at a slow network — it is the honest
 * "nobody has asked yet" for this source**, and the caller is told to render
 * nothing while it holds. Seeding a `null` link with `loading: false` instead
 * would claim there is no mirror before anything had looked.
 */
function seedFor(source: TrackerSheetSource): TrackerSheetState {
  const settled = cached.get(source);
  return settled
    ? { loading: false, link: settled.spreadsheetUrl, previewLink: previewOf(settled) }
    : { loading: true, link: null, previewLink: null };
}

/**
 * The mirror's links, or `null` while unknown or when there is none.
 *
 * ⚠️ **`loading` exists so a caller can render NOTHING rather than a control
 * that appears and then vanishes.** Gate on `!loading && link !== null`: a
 * button that flashes in on first paint and disappears a moment later reads as
 * a bug in the app, and it is worse than a control that arrives a beat late.
 *
 * ⚠️ **`link` and `previewLink` are TWO answers and a caller must not substitute
 * one for the other.** `link` opens the document; `previewLink` is its read-only
 * form. They are `null` together against a current server, and `previewLink`
 * alone is `null` against an older one — which is why a caller that needs to
 * know whether this API can describe the mirror at all still gates on
 * `previewLink`.
 *
 * 🔴 **CORRECTED 2026-09-02 — `previewLink` IS NO LONGER "the only one that may
 * go in an iframe".** *SUPERSEDED: "`previewLink` is the read-only form and the
 * only one that may go in an iframe — the mirror is rewritten wholesale on a
 * timer, so an editable embed offers a cursor over cells whose contents are
 * about to be replaced."* The owner asked for an embed people can edit and
 * comment in; `ClientDeliverablesPage` frames `link` and falls back to
 * `previewLink`. **The hazard that sentence named is real and is now accepted
 * rather than disproved** — the reasoning, and what survives of it, is written
 * up once in `TrackerSheetPanel`, which is the file that renders the frame.
 */
export function useTrackerSheetLink(
  /**
   * Which mirror. ⚠️ **Defaulted to the client deliverables one so every caller
   * that predates the parameter is unchanged** — see the source union's header
   * for why this is a parameter and not a fourth copy of this file.
   *
   * Expected to be a constant at each call site; the render-time adjustment
   * below is what keeps it correct if one ever is not.
   */
  source: TrackerSheetSource = 'client-deliverables',
): TrackerSheetState {
  const { user } = useAuth();
  const signedIn = user !== null;

  /*
   * Seeded from the cache rather than from `null`, so a second mount in the
   * same tab — navigating back to the tracker, opening the mobile drawer —
   * renders the control on its FIRST paint instead of flashing empty and
   * filling in. There is nothing to wait for once the answer is known.
   */
  const [state, setState] = useState<TrackerSheetState>(() => seedFor(source));

  /*
   * ⚠️ **A CHANGED `source` MUST NOT KEEP SERVING THE OLD ANSWER, EVEN THOUGH
   * NO CALLER CHANGES IT TODAY.** Every call site passes a literal, so this
   * cannot fire now — and if one ever does, the failure without this is a page
   * framing the OTHER tracker's spreadsheet under its own heading, with nothing
   * erroring. That is exactly the hazard the two local forks were written to
   * avoid, so it is closed structurally rather than left to a rule.
   *
   * Adjusted during render rather than in an effect, which is the pattern
   * `FutureBillablesTrackerPage`'s sort-reset already uses here: an effect would
   * commit one render against the wrong source first.
   */
  const [lastSource, setLastSource] = useState(source);
  if (lastSource !== source) {
    setLastSource(source);
    setState(seedFor(source));
  }

  useEffect(() => {
    /*
     * Gated on the session for the reason `XeroProvider` records: an app-wide
     * reader that fires with no token sends a request that can only 401. Both
     * callers today sit behind `RequireAuth`, so this is belt-and-braces — but
     * it is the kind of guard whose absence is discovered by a 401 on the
     * sign-in screen, months later, by somebody who mounted it somewhere new.
     */
    if (!signedIn) return;

    let cancelled = false;

    /*
     * The guard the missing `AbortSignal` would otherwise provide: the shared
     * request runs to completion for whoever is still listening, and an
     * unmounted subscriber simply drops the answer.
     */
    void readSheetLink(source).then((sheet) => {
      if (cancelled) return;
      setState({ loading: false, link: sheet.spreadsheetUrl, previewLink: previewOf(sheet) });
    });

    return () => {
      cancelled = true;
    };
  }, [signedIn, source]);

  /*
   * Signed out, the answer is DERIVED at render rather than written from the
   * effect. Setting it there would start a second render synchronously — what
   * `react(set-state-in-effect)` exists to catch — and `XeroProvider` records
   * the same reasoning for the same shape.
   *
   * `loading` is FALSE, not true: "nobody asked" is a settled answer, and a
   * caller that waits on it would wait for ever.
   */
  return signedIn ? state : { loading: false, link: null, previewLink: null };
}

/*
 * ⚠️ THERE IS DELIBERATELY NO `reset()` FOR THE CACHE, AND SIGN-OUT DOES NOT
 * CLEAR IT. Every other per-session thing in this app is reset on an identity
 * change — `resetPreferenceBaselines()` runs on all three of them — because it
 * belongs to a person. This does not: it is derived from environment variables
 * on the server and is the SAME answer for every account. There is no other
 * user's value for it to leak. Add a reset the day it becomes per-user, and not
 * before, because a reset that clears nothing still has to be kept in step with
 * every call site.
 *
 * ⚠️ **A reset would now have to clear the MAPS, not a variable** — and clearing
 * one source's entry while leaving another's is the same wrong-document hazard
 * the keying closes. Clear both, or clear neither.
 */
